import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ChevronDown10pxSvg from "../../../assets/images/icons/ChevronDown10pxSvg";
import { useAuth } from "../../../context/AuthContext";
import AddEmployeesIcon24pxSvg from "./../../../assets/images/icons/AddEmployeesIcon24pxSvg";
import FixAttendanceIcon24pxSvg from "./../../../assets/images/icons/FixAttendanceIcon24pxSvg";
import RegisterDevice24pxSvg from "./../../../assets/images/icons/RegisterDevice24pxSvg";
import CustomSelect from "./../../../components/CustomSelect";
import CustomPercentLinearProgress from "./CustomPercentLinearProgress";
import DevicesChart from "./DevicesChart";
import EmployeesChart from "./EmployeesChart";
import GridInfoTable from "./GridInfoTable";
import MonthEventsCalendar from "./MonthEventsCalendar";
import ShowInfoEmployeeDialog from "./ShowInfoEmployeeDialog";
import { getListEntitieById } from "../../../axios/Setting/SettingUrlApi";
import ShowInfoDeviceDialog from "./ShowInfoDeviceDialog";
import { ControlpanelSuccess2thButton, ControlpanelSuccess3thButton } from "../../../components/StyledButton";
import ArrowRepeatIcon16pxSvg from "../../../assets/images/icons/ArrowRepeatIcon16pxSvg";
import { getListGroup } from "../../../axios/Group/GroupUrlApi";
import { toast } from "react-toastify";
import { LoadingProvider } from "../../../context/LoadingContext";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import { useRefresh } from "../../../context/RefreshContext";
import { useLocation } from "react-router-dom";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  startOfWeek
} from 'date-fns';

const planStats = {
  total: 100,
  used: 50,
  get percentUsed() {
    return (this.used / this.total) * 100;
  },
};

const CounDown = ({ handleRefreshData }) => {
  const { dashboardRefreshInterval } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // Setting load async sau F5; ban đầu AuthContext = 0 → không dùng làm count ngay
  const intervalSeconds = useMemo(() => {
    const minutes = Number(dashboardRefreshInterval);
    if (!Number.isFinite(minutes) || minutes <= 0) return 0;
    return Math.round(minutes * 60);
  }, [dashboardRefreshInterval]);

  const [count, setCount] = useState(0);
  const hasRefreshed = useRef(false);
  // Chỉ refresh khi đếm từ >0 về 0, tránh F5/init lúc count=0
  const canRefreshOnZero = useRef(false);

  // Sync countdown khi setting đã có giá trị hợp lệ
  useEffect(() => {
    if (intervalSeconds <= 0) return;
    canRefreshOnZero.current = false;
    hasRefreshed.current = false;
    setCount(intervalSeconds);
  }, [intervalSeconds]);

  useEffect(() => {
    if (intervalSeconds <= 0) return undefined;

    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev <= 0) return 0;
        if (prev === 1) {
          canRefreshOnZero.current = true;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [intervalSeconds]);

  useEffect(() => {
    if (intervalSeconds <= 0) return;
    if (count > 0) {
      hasRefreshed.current = false;
      return;
    }

    // count === 0 nhưng chưa từng đếm hết chu kỳ (F5 / vừa sync setting)
    if (!canRefreshOnZero.current) return;

    if (location.pathname === "/controlpanel/dashboard" && !hasRefreshed.current) {
      handleRefreshData();
      hasRefreshed.current = true;
    }

    canRefreshOnZero.current = false;
    setCount(intervalSeconds);
  }, [count, location.pathname, intervalSeconds, handleRefreshData]);

  const displayCount = intervalSeconds > 0 ? count : "—";

  return (
    <div className="mt-2 text-sm flex gap-1 text-[#6B7280]">
      <p>{t("common.statistics-will-be-refreshed-in")}</p>
      <p className="font-medium">
        <span className="font-semibold">
          {displayCount} {t("common.seconds")}.
        </span>
      </p>
    </div>
  );
};

const DashBoard = () => {
  const { refreshData, handleRefreshData } = useRefresh();
  const { profile } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector((state) => state.languageR.language);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogListEmployee, setDialogListEmployee] = useState([]);
  const [dialogListDevice, setDialogListDevice] = useState([]);
  const [dialogStatusType, setDialogStatusType] = useState("CHECK_IN");
  const [openShowInfoDialog, setOpenShowInfoDialog] = useState(false);
  const [openShowInfoDeviveDialog, setOpenShowInfoDeviceDialog] =
    useState(false);
  const [formData, setFormData] = useState({
    groupId: null,
  });
  const [groupData, setGroupData] = useState([]);
  const [dispalyWeek, setDispalyWeek] = useState(0);
  const currentLang = i18n.language;
  const [selectedDate, setSelectedDate] = useState(new Date());

  const calculateDisplayedWeeks = (date, weekStartsOn = 1) => {
    const firstDayOfMonth = startOfMonth(date);
    const lastDayOfMonth = endOfMonth(date);

    const calendarStartDate = startOfWeek(firstDayOfMonth, { weekStartsOn });

    const daysInGrid = eachDayOfInterval({
      start: calendarStartDate,
      end: lastDayOfMonth,
    });

    const totalCells = daysInGrid.length <= 35 ? 35 : 42;

    setDispalyWeek(totalCells / 7);
    return totalCells / 7;
  };

  const fetchData = async () => {
    const payload = {
      pageNo: 1,
      recordPerPage: 500,
      langId: localStorage.getItem("i18nextLngId"),
    };
    try {
      const response = await getListGroup(payload);

      const listUserGroupRaw = response.data.data || [];

      const listUserGroup = listUserGroupRaw.map((item) => ({
        ...item,
        groupId: item.id,
      }));

      setGroupData(listUserGroup);

      if (listUserGroup.length > 0) {
        const allUsersGroup =
          listUserGroup.find(
            (g) => g.groupName?.trim().toLowerCase() === "all users"
          ) || listUserGroup[0];

        setFormData((prev) => ({
          ...prev,
          groupId: allUsersGroup.groupId,
        }));
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t("common.network-timeout"));
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [t]);

  const displayItems = useMemo(
    () => [
      {
        icon: (
          <div className="flex items-center justify-center rounded-full p-4 bg-[#5B93FF] bg-opacity-10">
            <AddEmployeesIcon24pxSvg />
          </div>
        ),
        title: t("common.add"),
        subtitle: t("common.employees"),
        onClick: () =>
          navigate("/controlpanel/user-management", {
            state: { openAddEmployeeDialog: true },
          }),
      },
      {
        icon: (
          <div className="flex items-center justify-center rounded-full p-4 bg-[#FFD66B] bg-opacity-10">
            <FixAttendanceIcon24pxSvg />
          </div>
        ),
        title: t("common.fix"),
        subtitle: t("common.attendance"),
        onClick: () => navigate("/controlpanel/event-log-management"),
      },
      {
        icon: (
          <div className="flex items-center justify-center rounded-full p-4 bg-[#605BFF] bg-opacity-10">
            <RegisterDevice24pxSvg />
          </div>
        ),
        title: t("common.register"),
        subtitle: t("common.devices"),
        onClick: () => navigate("/controlpanel/devices-management"),
      },
    ],
    [navigate, i18n.language, t]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCloseShowInfo = () => {
    setOpenShowInfoDialog(false);
  };

  const handleCloseShowInfoDevice = () => {
    setOpenShowInfoDeviceDialog(false);
  };

  useEffect(() => {
    if (!groupData.length) return;
    if (formData.groupId != null) return;

    const allUsersGroup =
      groupData.find(
        (g) => g.groupName?.trim().toLowerCase() === "all users"
      ) || groupData[0];

    setFormData((prev) => ({
      ...prev,
      groupId: allUsersGroup.groupId,
    }));
  }, [groupData, formData.groupId]);

  return (
    <div className="dashboard">
      {/* ✅ Mobile: 1 cột, 2XL: chia 7/5 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
        <div className="flex flex-col gap-0">
          <p className="text-lg md:text-xl font-semibold">
            {t("common.welcome-back")},{" "}
            <span className="break-words">{profile.fullName}!</span>
          </p>
          <CounDown handleRefreshData={handleRefreshData} />
        </div>


        <div data-tour="dashboard-group-filter" className="flex flex-wrap items-center gap-3">

          <div className="relative inline-flex items-center">
            <ChevronDown10pxSvg className="absolute top-[2px] right-0 m-3 pointer-events-none" />
            <CustomSelect
              name="groupId"
              value={formData.groupId ?? ""}
              onChange={handleInputChange}
              options={groupData}
              dataType="group"
              className="min-w-[160px] h-10 bg-white"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",

                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                },

                "& .MuiSelect-select": {
                  backgroundColor: "#fff",
                },
              }}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 2xl:grid-cols-12 gap-4 items-stretch">
        {/* RIGHT */}
        <div className="2xl:col-span-8 grid grid-rows-[auto_1fr] gap-4">
          {/* Charts: mobile 1 cột, md: 2 cột */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 shrink-0">
            <div>
              <LoadingProvider>
                <div data-tour="dashboard-employees-chart">
                  <EmployeesChart
                    refreshData={refreshData}
                    setDialogTitle={setDialogTitle}
                    setDialogListEmployee={setDialogListEmployee}
                    setDialogStatusType={setDialogStatusType}
                    setOpenShowInfoDialog={setOpenShowInfoDialog}
                  />
                </div>
              </LoadingProvider>

            </div>
            <div>
              <LoadingProvider>
                <div data-tour="dashboard-devices-chart">
                  <DevicesChart
                    refreshData={refreshData}
                    setDialogTitle={setDialogTitle}
                    setDialogListDevice={setDialogListDevice}
                    setOpenShowInfoDeviceDialog={setOpenShowInfoDeviceDialog}
                  />
                </div>
              </LoadingProvider>
            </div>
          </div>

          {/* Calendar chiếm phần còn lại */}
          <div className="flex-1 min-h-0">
            <LoadingProvider><div data-tour="dashboard-calendar">
              <MonthEventsCalendar
                refreshData={refreshData}
                groupId={formData.groupId}
                calculateDisplayedWeeks={calculateDisplayedWeeks}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              /></div></LoadingProvider>
          </div>
        </div>
        {/* LEFT */}
        <div className="2xl:col-span-4 flex flex-col gap-4 h-full">
          {/* Bảng GridInfoTable */}
          <div className="">
            <LoadingProvider><div>
              <GridInfoTable
                refreshData={refreshData}
                groupId={formData.groupId}
                dispalyWeek={dispalyWeek}
                selectedDate={selectedDate}
              /></div></LoadingProvider>
          </div>
        </div>
      </div>

      <LoadingProvider><ShowInfoEmployeeDialog
        open={openShowInfoDialog}
        handleClose={handleCloseShowInfo}
        title={dialogTitle}
        listEmployee={dialogListEmployee}
        statusType={dialogStatusType}
      /></LoadingProvider>

      <LoadingProvider><ShowInfoDeviceDialog
        open={openShowInfoDeviveDialog}
        handleClose={handleCloseShowInfoDevice}
        title={dialogTitle}
        listDevice={dialogListDevice}
      /></LoadingProvider>

    </div>
  );
};

export default DashBoard;
